import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Search, Plus, Tag, X, PackageSearch, Upload, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import RequirePermission from '../../../../components/AccessControl/RequirePermission';
import { useStore } from '../../../../context/StoreContext';
import { loadServices, servicesFetch, errorText } from '../../pricelistUtils';
import styles from './PricelistOverview.module.css';
import PricelistTable from '../PricelistTable/PricelistTable';

const SKELETON_ROWS = 7;

// Service price list: one tab per audience section (Men, Women, Kids...), grouped by category
const PricelistOverview = () => {
    const { activeStore } = useStore();
    const navigate = useNavigate();
    const storeId = activeStore?.id || activeStore?._id;
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('All');
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [msg, setMsg] = useState(null);

    const fetchServices = useCallback(async () => {
        if (!storeId || storeId === 'global_hq') { setItems([]); return; }
        setIsLoading(true);
        try { setItems(await loadServices(storeId)); setMsg(null); } catch (e) { setMsg({ type: 'error', text: errorText(e) }); } finally { setIsLoading(false); }
    }, [storeId]);
    useEffect(() => { fetchServices(); }, [fetchServices]);

    const tabs = useMemo(() => ['All', ...[...new Set(items.map((i) => i.targetDemographic).filter(Boolean))].sort()], [items]);
    const counts = useMemo(() => items.reduce((c, i) => ({ ...c, [i.targetDemographic]: (c[i.targetDemographic] || 0) + 1 }), { All: items.length }), [items]);
    useEffect(() => { if (!tabs.includes(activeTab)) setActiveTab('All'); }, [tabs, activeTab]);

    const q = searchTerm.trim().toLowerCase();
    const filtered = items.filter((i) => (activeTab === 'All' || i.targetDemographic === activeTab)
        && (!q || [i.name, i.category, i.targetDemographic].some((v) => String(v || '').toLowerCase().includes(q))));

    const handleEdit = (item) => navigate(`/pricelist/service/add?serviceId=${encodeURIComponent(item.id)}`);
    const handleAddons = (service) => navigate(`/pricelist/add?serviceId=${encodeURIComponent(service.id)}`);
    const handleAddInCategory = (category, audience) => {
        const params = new URLSearchParams({ category });
        const aud = audience || (activeTab !== 'All' ? activeTab : '');
        if (aud) params.set('demographic', aud);
        navigate(`/pricelist/service/add?${params.toString()}`);
    };
    const handleDelete = async (id) => {
        const s = items.find((x) => x.id === id);
        if (!window.confirm(`Remove "${s?.name}" (${s?.targetDemographic}) from the price list? Old bills keep showing it.`)) return;
        try {
            const j = await servicesFetch(`/${id}`, { method: 'DELETE', storeId });
            if (!j.success) throw new Error(j.message);
            setItems((list) => list.filter((x) => x.id !== id));
            setMsg({ type: 'ok', text: j.message || 'Service removed.' });
        } catch (e) { setMsg({ type: 'error', text: errorText(e) }); }
    };

    const showEmpty = !isLoading && filtered.length === 0;

    return (
        <div className={styles.pageWrapper}>
            <div className={styles.fixedHeader}>
                <div className={styles.headerTopRow}>
                    <div className={styles.titleSection}>
                        <div className={styles.titleMark}><Tag size={18} /></div>
                        <div className={styles.titleTextGroup}>
                            <h1 className={styles.pageTitle}>Service price list</h1>
                            <p className={styles.pageSubtitle}>{items.length} services in {tabs.length - 1} {tabs.length - 1 === 1 ? 'section' : 'sections'}.</p>
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <RequirePermission module="pricelist" action="import">
                            <button type="button" className={styles.primaryBtn} style={{ background: '#fff', color: 'var(--h-color-primary-700, #471ea7)', border: '1px solid var(--h-color-primary-200, #d9dbff)' }} onClick={() => navigate('/pricelist/master')}>
                                <Upload size={14} /> <span>Import Excel</span>
                            </button>
                        </RequirePermission>
                        <RequirePermission module="pricelist" action="create">
                            <button type="button" className={styles.primaryBtn} onClick={() => handleAddInCategory('', '')}><Plus size={14} /> <span>New service</span></button>
                        </RequirePermission>
                    </div>
                </div>

                <div className={styles.headerBottomRow}>
                    <div className={styles.tabsContainer}>
                        {tabs.map((tab) => (
                            <button key={tab} type="button" className={`${styles.tabBtn} ${activeTab === tab ? styles.activeTab : ''}`} onClick={() => setActiveTab(tab)}>
                                <span>{tab}</span><span className={styles.tabCount}>{counts[tab] || 0}</span>
                            </button>
                        ))}
                    </div>
                    <div className={styles.controlsSection}>
                        <div className={styles.searchBox}>
                            <Search size={14} className={styles.searchIcon} />
                            <input type="text" placeholder="Search service, category, section..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className={styles.searchInput} />
                            {searchTerm && <button type="button" className={styles.clearSearchBtn} onClick={() => setSearchTerm('')} aria-label="Clear search"><X size={11} /></button>}
                        </div>
                    </div>
                </div>
                {msg && (
                    <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, padding: '8px 12px', borderRadius: 10, fontSize: 12.5, fontWeight: 800, background: msg.type === 'ok' ? 'var(--h-color-success-50, #f1f9f3)' : 'var(--h-color-danger-50, #fff4f3)', color: msg.type === 'ok' ? 'var(--h-color-success-600, #007a49)' : 'var(--h-color-danger-600, #be1025)' }}>
                        {msg.type === 'ok' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}<span style={{ flex: 1 }}>{msg.text}</span>
                        {msg.type === 'error' && <button type="button" onClick={fetchServices} style={{ border: 'none', background: 'none', color: 'inherit', fontWeight: 900, cursor: 'pointer' }}>Retry</button>}
                        <button type="button" onClick={() => setMsg(null)} style={{ border: 'none', background: 'none', color: 'inherit', cursor: 'pointer', display: 'grid' }} aria-label="Close"><X size={13} /></button>
                    </div>
                )}
            </div>

            <div className={styles.contentArea}>
                <div className={styles.tableShell}>
                    {isLoading ? (
                        <div className={styles.loaderBox}>
                            {Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                                <div className={styles.skeletonRow} key={i}>
                                    {['22%', '14%', '10%', '10%'].map((w) => <div key={w} className={styles.skeletonBlock} style={{ width: w }} />)}
                                    <div className={styles.skeletonBlock} style={{ width: '12%', marginLeft: 'auto' }} />
                                </div>
                            ))}
                        </div>
                    ) : showEmpty ? (
                        <div className={styles.emptyState}>
                            <div className={styles.emptyIconWrap}><PackageSearch size={20} /></div>
                            <p className={styles.emptyTitle}>{items.length ? 'No services match' : 'No services yet'}</p>
                            <p className={styles.emptyText}>{items.length ? 'Try another search or section.' : 'Upload your whole menu (Men, Women, Kids together) from one Excel file, or add services one by one.'}</p>
                            {!items.length && (
                                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                                    <RequirePermission module="pricelist" action="import">
                                        <button type="button" className={styles.emptyActionBtn} onClick={() => navigate('/pricelist/master')}><Upload size={13} /> <span>Import Excel</span></button>
                                    </RequirePermission>
                                    <RequirePermission module="pricelist" action="create">
                                        <button type="button" className={styles.emptyActionBtn} onClick={() => handleAddInCategory('', '')}><Plus size={13} /> <span>Add a service</span></button>
                                    </RequirePermission>
                                </div>
                            )}
                        </div>
                    ) : (
                        <PricelistTable data={filtered} showAudience={activeTab === 'All'} onEdit={handleEdit} onDelete={handleDelete} onManageAddons={handleAddons} onAddServiceInCategory={handleAddInCategory} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default PricelistOverview;
